import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Mail, RefreshCw, Send, PenSquare, Inbox, CheckCircle2, Plug, Unplug, Reply,
} from "lucide-react";

function timeLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
      " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

const ComposeDialog = ({ open, setOpen, onSent }) => {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/email/send", { to, subject, body });
      toast.success(`Email sent via ${r.data.via === "outlook" ? "Outlook" : "Resend"}`);
      setTo(""); setSubject(""); setBody("");
      setOpen(false);
      onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="email-compose-btn" className="gap-1.5">
          <PenSquare className="w-4 h-4" /> Compose
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>New email</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="email-compose-to" placeholder="recipient@example.com"
            value={to} onChange={(e) => setTo(e.target.value)} />
          <Input data-testid="email-compose-subject" placeholder="Subject"
            value={subject} onChange={(e) => setSubject(e.target.value)} />
          <Textarea data-testid="email-compose-body" placeholder="Write your message…"
            rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        <DialogFooter>
          <Button data-testid="email-compose-send" onClick={send}
            disabled={sending || !to || !subject || !body} className="gap-1.5">
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminEmail() {
  const [status, setStatus] = useState(null);
  const [folder, setFolder] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);

  const loadStatus = useCallback(async () => {
    try { setStatus((await api.get("/admin/email/status")).data); } catch { /* noop */ }
  }, []);

  const loadMessages = useCallback(async (f) => {
    try {
      setMessages((await api.get(`/admin/email/messages?folder=${f}`)).data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadMessages(folder); setSelected(null); }, [folder, loadMessages]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected")) toast.success("Outlook mailbox connected");
    if (p.get("outlook_error")) toast.error("Outlook connection failed — try again");
  }, []);

  const openMessage = async (m) => {
    try {
      const r = await api.get(`/admin/email/messages/${m.id}`);
      setSelected(r.data);
      setReplyBody("");
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_read: true } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const connectOutlook = async () => {
    try {
      const r = await api.get("/admin/email/outlook/connect");
      window.location.assign(r.data.auth_url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disconnectOutlook = async () => {
    try {
      await api.post("/admin/email/outlook/disconnect");
      toast.success("Outlook disconnected");
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/email/sync");
      toast.success(`Synced — ${r.data.new_messages} new message${r.data.new_messages === 1 ? "" : "s"}`);
      loadMessages(folder);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const sendReply = async () => {
    if (!selected) return;
    setReplying(true);
    try {
      const r = await api.post(`/admin/email/messages/${selected.id}/reply`, { body: replyBody });
      toast.success(`Reply sent via ${r.data.via === "outlook" ? "Outlook" : "Resend"}`);
      setReplyBody("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReplying(false); }
  };

  return (
    <div data-testid="admin-email-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6" /> Email
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Send and receive email from the dashboard.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status?.outlook_connected ? (
            <>
              <span data-testid="email-outlook-status"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Outlook: {status.mailbox || "connected"}
              </span>
              <Button data-testid="email-sync-btn" variant="outline" size="sm" onClick={syncNow} disabled={syncing} className="gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync
              </Button>
              <Button data-testid="email-outlook-disconnect" variant="ghost" size="sm" onClick={disconnectOutlook} className="gap-1.5 text-slate-500">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </>
          ) : status?.outlook_configured ? (
            <Button data-testid="email-outlook-connect" variant="outline" size="sm" onClick={connectOutlook} className="gap-1.5">
              <Plug className="w-3.5 h-3.5" /> Connect Outlook
            </Button>
          ) : null}
          <ComposeDialog open={composeOpen} setOpen={setComposeOpen}
            onSent={() => { setFolder("sent"); loadMessages("sent"); }} />
        </div>
      </div>

      {status && !status.outlook_configured && (
        <div data-testid="email-outlook-setup-banner"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <b>Outlook sync not configured.</b> Sending works now via Resend (from info@noderahealth.com).
          To read and send from your Outlook mailbox, register an app in Microsoft Azure and add
          <code className="mx-1 text-xs bg-amber-100 px-1 py-0.5 rounded">MS_CLIENT_ID</code>,
          <code className="mx-1 text-xs bg-amber-100 px-1 py-0.5 rounded">MS_CLIENT_SECRET</code>,
          <code className="mx-1 text-xs bg-amber-100 px-1 py-0.5 rounded">MS_TENANT_ID</code> and
          <code className="mx-1 text-xs bg-amber-100 px-1 py-0.5 rounded">MS_REDIRECT_URI</code> to the backend environment.
        </div>
      )}

      <div className="flex gap-2">
        {[
          { id: "inbox", label: "Inbox", icon: Inbox },
          { id: "sent", label: "Sent", icon: Send },
        ].map((t) => (
          <button key={t.id} data-testid={`email-folder-${t.id}`}
            onClick={() => setFolder(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              folder === t.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5">
        <div data-testid="email-message-list" className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100 max-h-[65vh] overflow-y-auto">
          {messages.length === 0 && (
            <div data-testid="email-empty-state" className="p-8 text-center text-sm text-slate-400">
              {folder === "inbox"
                ? (status?.outlook_connected ? "Inbox is empty — hit Sync to pull from Outlook." : "No messages yet. Once Outlook sync is set up, your mailbox will appear here.")
                : "No sent emails yet."}
            </div>
          )}
          {messages.map((m) => (
            <button key={m.id} data-testid={`email-row-${m.id}`} onClick={() => openMessage(m)}
              className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${
                selected?.id === m.id ? "bg-blue-50/60" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`text-sm truncate ${m.is_read ? "text-slate-600" : "font-semibold text-slate-900"}`}>
                  {folder === "inbox" ? (m.from_name || m.from_email || "Unknown") : m.to_email}
                </span>
                <span className="text-[11px] text-slate-400 shrink-0">{timeLabel(m.received_at || m.created_at)}</span>
              </div>
              <div className={`text-sm truncate ${m.is_read ? "text-slate-500" : "font-medium text-slate-800"}`}>{m.subject}</div>
              <div className="text-xs text-slate-400 truncate mt-0.5">{m.preview}</div>
            </button>
          ))}
        </div>

        <div data-testid="email-reading-pane" className="rounded-xl border border-slate-200 bg-white p-6 min-h-[300px]">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              Select a message to read it
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900">{selected.subject}</h2>
                <div className="text-xs text-slate-500 mt-1">
                  From <b>{selected.from_name || selected.from_email}</b>
                  {selected.from_email && selected.from_name ? ` <${selected.from_email}>` : ""} · to {selected.to_email} · {timeLabel(selected.received_at || selected.created_at)}
                </div>
              </div>
              <div className="border-t border-slate-100 pt-4 text-sm text-slate-700 leading-relaxed">
                {selected.body_html ? (
                  <iframe title="email-body" sandbox="" srcDoc={selected.body_html}
                    className="w-full min-h-[320px] border-0" />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans">{selected.body_text || selected.preview}</pre>
                )}
              </div>
              {selected.folder === "inbox" && selected.from_email && (
                <div className="border-t border-slate-100 pt-4 space-y-2">
                  <Textarea data-testid="email-reply-body" rows={4}
                    placeholder={`Reply to ${selected.from_email}…`}
                    value={replyBody} onChange={(e) => setReplyBody(e.target.value)} />
                  <Button data-testid="email-reply-send" size="sm" onClick={sendReply}
                    disabled={replying || !replyBody.trim()} className="gap-1.5">
                    <Reply className="w-3.5 h-3.5" /> {replying ? "Sending…" : "Send reply"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
